/** Bulk operations + Job duplicate + Nerd skills + Mass notify + ViaCEP. */
import { api } from "@/lib/api";

export interface BulkResult {
  requested: number;
  deleted: number;
  failed: string[];
}

export const bulkApi = {
  deleteJobs: async (ids: string[]): Promise<BulkResult> => {
    const { data } = await api.post<BulkResult>("/v1/jobs/bulk-delete", { ids });
    return data;
  },
  deleteClients: async (ids: string[]): Promise<BulkResult> => {
    const { data } = await api.post<BulkResult>("/v1/clients/bulk-delete", { ids });
    return data;
  },
  deleteNerds: async (ids: string[]): Promise<BulkResult> => {
    const { data } = await api.post<BulkResult>("/v1/nerds/bulk-delete", { ids });
    return data;
  },
  transitionJobs: async (ids: string[], status: string, reason?: string) => {
    const { data } = await api.post("/v1/jobs/bulk-transition", { ids, status, reason });
    return data;
  },
};

export const jobActionsApi = {
  duplicate: async (id: string): Promise<{ id: string; code: string }> => {
    const { data } = await api.post(`/v1/jobs/${id}/duplicate`);
    return data;
  },
  assignNerd: async (id: string, nerd_id: string, note?: string) => {
    const { data } = await api.post(`/v1/jobs/${id}/assign-nerd`, { nerd_id, note });
    return data;
  },
};

export interface NerdSkill {
  nerd_id: string;
  category_id: string;
  category_name: string | null;
  level: number;
  created_at: string;
}

export const nerdSkillsApi = {
  list: async (nerd_id: string): Promise<NerdSkill[]> => {
    const { data } = await api.get<NerdSkill[]>(`/v1/nerds/${nerd_id}/skills`);
    return data;
  },
  upsert: async (nerd_id: string, category_id: string, level: number): Promise<NerdSkill> => {
    const { data } = await api.put<NerdSkill>(`/v1/nerds/${nerd_id}/skills/${category_id}`, { category_id, level });
    return data;
  },
  remove: async (nerd_id: string, category_id: string): Promise<void> => {
    await api.delete(`/v1/nerds/${nerd_id}/skills/${category_id}`);
  },
};

export interface MassNotifyIn {
  target: "all" | "role" | "specific";
  role?: string;
  user_ids?: string[];
  title: string;
  body?: string;
  link?: string;
}

export const massNotifyApi = {
  broadcast: async (body: MassNotifyIn): Promise<{ recipients: number; target: string }> => {
    const { data } = await api.post("/v1/notifications/broadcast", body);
    return data;
  },
};

// ── ViaCEP (público) ─────────────────────────────────────────────────────
export interface ViaCepResult {
  cep: string;
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
  complemento?: string;
  erro?: boolean;
}

export async function fetchViaCep(cep: string): Promise<ViaCepResult | null> {
  const digits = cep.replace(/\D/g, "");
  if (digits.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!r.ok) return null;
    const data = await r.json();
    if (data.erro) return null;
    return data as ViaCepResult;
  } catch {
    return null;
  }
}
